import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sendToRoss, saveMessage, subscribeToMessages, uploadImage, styleTransferImage, saveArtworkToGallery, setActiveArtwork } from '../services/artService';
import { passTurn } from '../services/circleService';
import { handleAuthError } from '../utils/authErrorHandler';

function generateDisplayNameFromEmail(email) {
  if (!email) return 'Guest';
  return email.split('@')[0];
}

export function useArtChat(circleId, activeGame, currentCircle) {
  const { currentUser, userProfile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    if (!circleId) return;
    return subscribeToMessages(circleId, setMessages);
  }, [circleId]);

  const dismissLimitReached = useCallback(() => setLimitReached(null), []);

  const sendMessage = useCallback(async (text, { imageUrl = null, intent = null } = {}) => {
    if (!circleId || !currentUser) return;
    const isDirectedAtRoss = /@ross/i.test(text);
    const textForRoss = isDirectedAtRoss ? text.replace(/@ross\s*/gi, '').trim() : text;

    let resolvedImageUrl = imageUrl;
    let resolvedIntent = intent;
    if (isDirectedAtRoss && !resolvedImageUrl) {
      const lowerText = textForRoss.toLowerCase();
      const isEditRequest = /\b(edit|change|modify|adjust|tweak|update|redo|fix|transform|alter)\b/.test(lowerText);
      if (isEditRequest) {
        const latestArtwork = [...messagesRef.current].reverse().find(m => m.imageUrl && m.isNewArtwork);
        if (latestArtwork?.imageUrl) {
          resolvedImageUrl = latestArtwork.fullResImageUrl || latestArtwork.imageUrl;
          resolvedIntent = 'edit';
        }
      }
    }

    const userMessage = {
      id: crypto.randomUUID(),
      text,
      role: 'user',
      userId: currentUser.uid,
      displayName: userProfile?.displayName || currentUser.displayName || generateDisplayNameFromEmail(currentUser.email),
      imageUrl: resolvedImageUrl || null,
      isNewArtwork: false,
      isDirectedAtRoss,
      timestamp: Date.now(),
    };
    await saveMessage(circleId, userMessage);

    if (!isDirectedAtRoss) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await sendToRoss({
        message: textForRoss,
        messageHistory: messagesRef.current,
        imageUrl: resolvedImageUrl,
        intent: resolvedIntent,
        circleId,
      });

      if (response.limitReached) {
        setLimitReached({
          generationsUsed: response.generationsUsed,
          generationsLimit: response.generationsLimit,
          planId: response.planId,
        });
      }

      // AI Attribution: artwork is attributed to the user, not Ross
      if (response.generatedImageUrl) {
        const fallbackName = userProfile?.displayName || currentUser.displayName || generateDisplayNameFromEmail(currentUser.email);
        const artworkMessage = {
          id: crypto.randomUUID(),
          text: '',
          role: 'user',
          userId: currentUser.uid,
          displayName: fallbackName,
          imageUrl: response.generatedImageUrl,
          fullResImageUrl: response.fullResImageUrl || null,
          isNewArtwork: true,
          isAiAssisted: true,
          limitReached: response.limitReached || false,
          timestamp: Date.now(),
        };
        await saveMessage(circleId, artworkMessage);

        // Only add Ross comment for original creations (not edits)
        if (resolvedIntent !== 'edit' && response.reply) {
          const rossComment = {
            id: crypto.randomUUID(),
            text: response.reply,
            role: 'assistant',
            userId: 'ross',
            displayName: 'Ross',
            isNewArtwork: false,
            timestamp: Date.now(),
          };
          await saveMessage(circleId, rossComment);
        }
      } else {
        // Text-only response from Ross
        const rossMessage = {
          id: crypto.randomUUID(),
          text: response.reply || response.text || '',
          role: 'assistant',
          userId: 'ross',
          displayName: 'Ross',
          isNewArtwork: false,
          limitReached: response.limitReached || false,
          timestamp: Date.now(),
        };
        await saveMessage(circleId, rossMessage);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      const isAuthErr = handleAuthError(err);
      const errorMessage = {
        id: crypto.randomUUID(),
        text: isAuthErr ? "Your session expired. Please sign in again." : "Sorry, I had trouble with that. Let's try again!",
        role: 'assistant',
        userId: 'ross',
        displayName: 'Ross',
        isNewArtwork: false,
        timestamp: Date.now(),
      };
      await saveMessage(circleId, errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [circleId, currentUser, userProfile]);

  const uploadAndPostDrawing = useCallback(async (imageBlob) => {
    if (!circleId || !currentUser) return;
    setIsLoading(true);
    try {
      const url = await uploadImage(currentUser.uid, imageBlob);
      await setActiveArtwork(circleId, true);
      const fallbackName = userProfile?.displayName || currentUser.displayName || generateDisplayNameFromEmail(currentUser.email);
      
      // Build game-aware message text
      let messageText = `${fallbackName} added a drawing!`;
      if (activeGame && activeGame.players && activeGame.players.length > 0) {
        const nextDrawerIndex = (activeGame.players.indexOf(activeGame.currentDrawer) + 1) % activeGame.players.length;
        const nextDrawerUid = activeGame.players[nextDrawerIndex];
        const nextDrawerName = currentCircle?.members?.find(m => m.uid === nextDrawerUid)?.displayName || 'Next player';
        messageText = `${fallbackName} took their turn! It's ${nextDrawerName}'s turn now.`;
      }
      
      const drawingMessage = {
        id: crypto.randomUUID(),
        text: messageText,
        role: 'user',
        userId: currentUser.uid,
        displayName: fallbackName,
        imageUrl: url,
        isNewArtwork: true,
        timestamp: Date.now(),
      };
      await saveMessage(circleId, drawingMessage);

      // Auto-pass turn in game mode
      if (activeGame && activeGame.players && activeGame.players.length > 0) {
        const nextDrawerIndex = (activeGame.players.indexOf(activeGame.currentDrawer) + 1) % activeGame.players.length;
        const nextDrawerUid = activeGame.players[nextDrawerIndex];
        await passTurn(circleId, nextDrawerUid, activeGame.prompt);
      }

      // Show upgrade prompt if this is an anonymous user's first artwork
      if (currentUser.isAnonymous) {
        const userArtworks = messagesRef.current.filter(
          m => m.userId === currentUser.uid && m.isNewArtwork
        );
        if (userArtworks.length === 0) {
          setShowUpgradePrompt(true);
        }
      }
    } catch (err) {
      console.error('Failed to upload drawing:', err);
    } finally {
      setIsLoading(false);
    }
  }, [circleId, currentUser, userProfile, activeGame, currentCircle]);

  const saveToGallery = useCallback(async (message) => {
    if (!circleId || !currentUser) return;
    await saveArtworkToGallery({
      circleId,
      imageUrl: message.imageUrl,
      fullResImageUrl: message.fullResImageUrl || null,
      createdByUserId: message.userId,
      createdByDisplayName: message.displayName,
      savedByUserId: currentUser.uid,
      prompt: message.text || '',
      timestamp: message.timestamp,
    });
    await setActiveArtwork(circleId, false);
  }, [circleId, currentUser]);

  const styleTransfer = useCallback(async (imageUrl, style) => {
    if (!circleId || !currentUser) return;
    setIsLoading(true);
    try {
      const userMsg = {
        id: crypto.randomUUID(),
        text: `Transform to ${style.label}`,
        role: 'user',
        userId: currentUser.uid,
        displayName: userProfile?.displayName || currentUser.displayName || generateDisplayNameFromEmail(currentUser.email),
        imageUrl,
        isNewArtwork: false,
        timestamp: Date.now(),
      };
      await saveMessage(circleId, userMsg);

      const response = await styleTransferImage({ imageUrl, style, circleId });

      if (response.limitReached) {
        setLimitReached({
          generationsUsed: response.generationsUsed,
          generationsLimit: response.generationsLimit,
          planId: response.planId,
        });
      }

      // AI Attribution: style transfer is an edit, so artwork attributed to user, no Ross comment
      const fallbackName = userProfile?.displayName || currentUser.displayName || generateDisplayNameFromEmail(currentUser.email);
      const transformedMsg = {
        id: crypto.randomUUID(),
        text: '',
        role: 'user',
        userId: currentUser.uid,
        displayName: fallbackName,
        imageUrl: response.generatedImageUrl || null,
        isNewArtwork: !!response.generatedImageUrl,
        isAiAssisted: true,
        limitReached: response.limitReached || false,
        timestamp: Date.now(),
      };
      await saveMessage(circleId, transformedMsg);
    } catch (err) {
      console.error('Style transfer failed:', err);
      const isAuthErr = handleAuthError(err);
      const errorMsg = {
        id: crypto.randomUUID(),
        text: isAuthErr ? "Your session expired. Please sign in again." : "Sorry, I couldn't transform that image. Let's try again!",
        role: 'assistant',
        userId: 'ross',
        displayName: 'Ross',
        isNewArtwork: false,
        timestamp: Date.now(),
      };
      await saveMessage(circleId, errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [circleId, currentUser, userProfile]);

  return { messages, isLoading, sendMessage, uploadAndPostDrawing, styleTransfer, saveToGallery, limitReached, dismissLimitReached, showUpgradePrompt, setShowUpgradePrompt };
}
